import type { Location, NavigateFunction } from 'react-router-dom'

const PENDING_LOGIN_REDIRECT_KEY = 'lw_pending_login_redirect'
const LAST_AUTHENTICATED_PATH_KEY_PREFIX = 'lw_last_authenticated_path:'
const AUTHENTICATED_PATHNAMES = new Set([
  '/dashboard',
  '/chat',
  '/chat/coaching',
  '/wellness-goals',
  '/health-records',
  '/genetic-reports',
  '/genetic-reports/snps',
  '/genetic-reports/custom-reports',
  '/snp-library',
  '/messages',
  '/documents',
  '/wearables',
  '/support',
  '/profiles',
  '/account',
  '/account/profiles',
  '/account/sharing',
  '/account/affiliate',
  '/partnerships',
  '/partners/my-listing',
])

export function isAuthenticatedPathname(pathname: string): boolean {
  return AUTHENTICATED_PATHNAMES.has(pathname)
}

export function saveLastAuthenticatedPath(uid: string, pathname: string): void {
  if (!uid || !isAuthenticatedPathname(pathname)) return

  try {
    localStorage.setItem(`${LAST_AUTHENTICATED_PATH_KEY_PREFIX}${uid}`, pathname)
  } catch {
    // Storage privacy settings can prevent last-view persistence.
  }
}

export function resolveLastAuthenticatedDestination(uid: string): string {
  if (!uid) return '/chat'

  try {
    const key = `${LAST_AUTHENTICATED_PATH_KEY_PREFIX}${uid}`
    const pathname = localStorage.getItem(key)
    if (pathname && isAuthenticatedPathname(pathname)) return pathname
    if (pathname) localStorage.removeItem(key)
  } catch {
    return '/chat'
  }

  return '/chat'
}

export function buildReturnTo(pathname: string, search = ''): string {
  if (!search) return pathname
  const normalized = search.startsWith('?') ? search : `?${search}`
  return `${pathname}${normalized}`
}

export function splitReturnTo(returnTo: string): { pathname: string; search: string } {
  const queryIndex = returnTo.indexOf('?')
  if (queryIndex === -1) {
    return { pathname: returnTo, search: '' }
  }
  return {
    pathname: returnTo.slice(0, queryIndex),
    search: returnTo.slice(queryIndex),
  }
}

export function persistPendingLoginRedirect(returnTo: string): void {
  try {
    sessionStorage.setItem(PENDING_LOGIN_REDIRECT_KEY, returnTo)
  } catch {
    // sessionStorage unavailable in some browsers / privacy modes
  }
}

export function peekPendingLoginRedirect(): string | null {
  try {
    return sessionStorage.getItem(PENDING_LOGIN_REDIRECT_KEY)
  } catch {
    return null
  }
}

export function consumePendingLoginRedirect(): string | null {
  try {
    const value = sessionStorage.getItem(PENDING_LOGIN_REDIRECT_KEY)
    if (value) sessionStorage.removeItem(PENDING_LOGIN_REDIRECT_KEY)
    return value
  } catch {
    return null
  }
}

export function resolveLoginReturnTo(
  location: Location,
  searchParams: URLSearchParams,
): string {
  const redirectParam = searchParams.get('redirect')
  if (redirectParam?.startsWith('/')) {
    return redirectParam
  }

  const from = location.state?.from as
    | { pathname?: string; search?: string }
    | undefined
  if (from?.pathname) {
    if (from.search) {
      return buildReturnTo(from.pathname, from.search)
    }
    if (from.pathname.includes('?')) {
      return from.pathname
    }
    return from.pathname
  }

  const pending = peekPendingLoginRedirect()
  if (pending) return pending

  return '/redirect-to-profile'
}

export function navigateToLogin(
  navigate: NavigateFunction,
  returnTo: string,
): void {
  persistPendingLoginRedirect(returnTo)
  const { pathname, search } = splitReturnTo(returnTo)
  navigate(`/login?redirect=${encodeURIComponent(returnTo)}`, {
    state: { from: { pathname, search } },
  })
}
